import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext } from 'react';
import { ActivityOrigin } from 'libs.ecoa_shared';
import { H5, useLocalizeMessage, Text, RadioGroup, RadioGroupOption, Icon, Badge, Tooltip } from 'libs.nucleus';
import { AuthContext } from 'libs.react_internal';
// Extract options configuration for better maintainability
const createOptions = (origin, isSourceTranslationsAvailable, translate) => [
    {
        label: _jsx("span", { className: 'font-sans m-0 text-sm flex items-center gap-1', children: translate('Medable format') }),
        description: translate('Import Medable files (.json or .zip) using standard import.'),
        value: 'MEDABLE_FORMAT',
        dataTestId: 'medable-format',
        isInternal: false,
    },
    {
        label: (_jsxs("span", { className: 'font-sans m-0 text-sm flex items-center gap-1', children: [translate('Non-Medable format'), _jsx(Icon, { name: 'sparklesSolid', color: 'primary', size: 'sm' }), _jsx(Badge, { label: translate('Internal'), variant: 'common' })] })),
        description: translate('Import external files (.doc, .docx, .xlsx, .json) using AI import.'),
        value: 'NON_MEDABLE_FORMAT',
        dataTestId: 'non-medable-format',
        isInternal: true,
    },
    {
        label: _jsx("div", { className: 'font-sans m-0 text-sm flex items-center gap-1', children: translate('From the source') }),
        description: translate('Copy existing Medable translations from the source.'),
        value: origin === ActivityOrigin.VERSIONED
            ? 'MEDABLE_FORMAT_EXISTING_VERSION'
            : 'MEDABLE_FORMAT_EXISTING_CLONE',
        dataTestId: 'medable-format-existing-versioned',
        isInternal: false,
        disabled: (origin !== ActivityOrigin.VERSIONED && origin !== ActivityOrigin.CLONED) ||
            ((origin === ActivityOrigin.VERSIONED || origin === ActivityOrigin.CLONED) && !isSourceTranslationsAvailable),
    },
];
export const ImportDocumentTypeSelection = ({ documentType, setDocumentType, origin, isSourceTranslationsAvailable, }) => {
    const translate = useLocalizeMessage();
    const { isInternalUser } = useContext(AuthContext);
    const options = createOptions(origin, isSourceTranslationsAvailable, translate);
    const handleOptionClick = (option) => {
        if (option.disabled) {
            return; // Prevent selection of disabled options
        }
        setDocumentType(option.value);
    };
    const renderOption = (option) => {
        const isDisabled = option.disabled;
        const isSelected = !isDisabled && documentType === option.value;
        const optionContent = (_jsx("div", { className: `w-full p-6 border border-solid rounded-lg transition-all duration-200 ${isDisabled
                ? 'bg-neutral-100 border-neutral-200 cursor-not-allowed opacity-60'
                : isSelected
                    ? 'bg-purple-50 border-purple-600 border-[1.5px] shadow-sm cursor-pointer'
                    : 'hover:bg-neutral-50 border-neutral-200 hover:border-neutral-300 cursor-pointer'}`, "data-testid": option.dataTestId, onClick: () => handleOptionClick(option), children: _jsx(RadioGroupOption, { option: option, disabled: isDisabled }) }, option.value));
        // Add tooltip for disabled option
        if (isDisabled) {
            let tooltipMessage;
            if (origin !== ActivityOrigin.VERSIONED && origin !== ActivityOrigin.CLONED) {
                tooltipMessage = translate('This option is available when the activity was versioned or cloned. This is the first instance of the activity.');
            }
            else if (!isSourceTranslationsAvailable) {
                tooltipMessage = translate('This option is unavailable because source translations are not available.');
            }
            else {
                tooltipMessage = translate('This option is currently unavailable');
            }
            return (_jsx(Tooltip, { title: tooltipMessage, placement: 'top', children: optionContent }, option.value));
        }
        return optionContent;
    };
    return (_jsxs("div", { className: 'flex flex-col w-full max-w-2xl mx-auto h-full justify-center gap-y-3', children: [_jsxs("header", { children: [_jsx(H5, { children: translate('Document type') }), _jsx(Text, { as: 'span', size: 'sm', className: 'text-text-secondary', children: translate('Choose the format of your document to import.') })] }), _jsx(RadioGroup, { onChange: setDocumentType, value: documentType, children: options.filter((option) => !option.isInternal || isInternalUser).map(renderOption) })] }));
};
